import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import * as masterServices from "../../../services/masterServices";
import no_image_available from "../../../assets/pictures/no_image_available.jpg";
import { CustomNotification } from "../../../util/CustomNotification";
import { TableHelper } from "../../../util/TableHelper";
import { ModuleAndIdMapping, Modules } from "../../../constants/DatabaseConstants/MasterConstant";
import Authentication from "../../../util/Authentication";
import { allowedCustomerDropdownList, allowedLocationDropdownList } from "../../../constants/ApplicationConstants/MasterConstant";
import useValue from "../../../hooks/useValue";
import useTablePagination from "../../../hooks/useTablePagination";
import useGenerateColumns from "../../../hooks/useGenerateColumns";
import MasterUtil from "../../../util/MasterUtil";

import type FixMeLater from "../../../shared/types/fixMeLater.type";
import type { RootState } from "../../../appRedux/store";
import type { DisplayMasterProps } from "../../../shared/interface/MasterManagement/masterManagementCommon.interface";
import type { TableColumn } from "../../../shared/interface/MissedEntries.interface";

export const useDisplayMaster = (props: DisplayMasterProps) => {
  /* CONSTANTS */

  /* STATES */
  const [tableData, setTableData] = useState([]);
  const [filteredTableData, setFilteredTableData] = useState<any[]>([]);
  const [visible, setVisible] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>();
  const [tableColumn, setTableColumn] = useState<TableColumn[]>();

  const { masterFilters } = useSelector(({ common }: RootState) => common);

  /* CUSTOM HOOKS */
  const { value: loading, handleDisableValue: handleDisableLoading, handleEnableValue: handleEnableLoading } = useValue(false);
  const { value: refresh, handleDisableValue: handleDisableRefresh, handleEnableValue: handleEnableRefresh } = useValue(false);
  const { pagination, handleChangePagination, handleChangeTotalSize, handleResetPagination } = useTablePagination(0);

  const { columns, rowId, rowData, actionType, handleClearRowIdAndActionType, imagePath, handleResetImagePath } = useGenerateColumns(masterFilters?.moduleId!);

  /* HELPER FUNCTIONS */

  /* HANDLERS */
  const handleImageError = () => {
    setImagePreview(no_image_available);
  };

  const handleFilterTableData = (tableData: FixMeLater[], searchedText: string) => {
    const notIncludeKeys = ["sr_no", "is_active", "actions", "logo_url"];
    const includeKeys = ["isActive"];

    /* Extract column name ( dataIndex ) */
    const columnFilterKeys = columns?.filter((column: FixMeLater) => !notIncludeKeys?.includes(column.dataIndex))?.map((column: FixMeLater) => column?.dataIndex);
    const keys = [...includeKeys, ...(columnFilterKeys || [])];

    /* Pass the filtered data to TableHelper for additional filtering */
    const result = TableHelper.getFilterDataSource(tableData, keys, searchedText) as FixMeLater[];

    /* update filter state */
    setFilteredTableData(result);

    /* update pagination */
    handleChangeTotalSize(result?.length);
  };

  /* EFFECTS  */
  useEffect(() => {
    props.handleAddBtnDisplay(true);

    const fetchOneMasterData = async () => {
      try {
        /* Reset Initial Page */
        handleResetPagination();

        /* Update Column */
        setTableColumn(columns);

        if (!refresh) handleEnableLoading();

        /* get Master all records active and inactive. pass : true */
        /* KeyFob ALlocation Master only get active records. pass : null  */
        const showDeleted = masterFilters?.moduleId === ModuleAndIdMapping[Modules.KEY_FOB_ALLOCATION_MASTER] ? null : true;

        const getModuleName = Authentication.getModuleNameById(masterFilters?.moduleId!);

        const selectedCustomerId = getModuleName && allowedCustomerDropdownList?.includes(getModuleName) && masterFilters?.customerId ? masterFilters?.customerId : null;
        const selectedLocationId = getModuleName && allowedLocationDropdownList?.includes(getModuleName) && masterFilters?.locationId ? masterFilters?.locationId : null;
        const selectedClientId = selectedCustomerId && selectedLocationId && masterFilters?.clientId ? masterFilters?.clientId : null;
        const selectedKeyFobIdId = selectedCustomerId && selectedLocationId && masterFilters?.keyFobId ? masterFilters?.keyFobId : null;

        const response = await masterServices.getOneMasterListById(masterFilters?.moduleId!, showDeleted, selectedCustomerId, selectedLocationId, selectedClientId, selectedKeyFobIdId);
        const formattedResult = await MasterUtil.formattedResponse(masterFilters?.moduleId!, response?.AllRecordsList);

        const finalResult = formattedResult?.map((item: any) => {
          return {
            ...item,
            isActive: item?.is_active ? "active" : "in-active"
          };
        });

        setTableData(finalResult);
        setFilteredTableData(finalResult);
        handleChangeTotalSize(finalResult?.length);
        props.handleAddBtnDisplay(false);
        handleFilterTableData(finalResult, props.searchedText);
      } catch (error) {
        setTableData([]);
        setFilteredTableData([]);
        handleChangeTotalSize(0);
        CustomNotification.showErrorMessage(error);
      } finally {
        handleDisableRefresh();
        handleDisableLoading();
      }
    };

    if (refresh || (masterFilters?.refresh && masterFilters?.moduleId)) fetchOneMasterData();

    // eslint-disable-next-line
  }, [refresh, masterFilters?.moduleId, masterFilters?.customerId, masterFilters?.locationId, masterFilters?.clientId, masterFilters?.keyFobId, masterFilters?.refresh]);

  useEffect(() => {
    handleFilterTableData(tableData, props.searchedText);
  }, [props.searchedText]);

  useEffect(() => {
    if (imagePath) {
      setImagePreview(imagePath);
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [imagePath]);

  return {
    loading,
    masterFilters,
    rowId,
    actionType,
    columns: tableColumn,
    filteredTableData,
    pagination,
    handleChangePagination,
    rowData,
    handleEnableRefresh,
    handleClearRowIdAndActionType,
    imagePreview,
    handleImageError,
    visible,
    setVisible,
    handleResetImagePath
  };
};
